<template>
  <div class="page">
    <div class="page__header">
      <div class="page__header-title">
        Edit Video
        <div class="page__header-subtitle">Add thumbnails and JSON</div>
      </div>
    </div>
    <div class="page__body">
      <hr />
      <form
        class="text-light"
        v-if="videoToEdit"
        ref="form"
        autocomplete="off"
        @submit.prevent="videoUpdate()"
      >
        <b-row>
          <b-col>
            <div class="video">
              <div>
                <b-embed
                  v-if="video.videoURL"
                  type="video"
                  aspect="16by9"
                  :src="video.videoURL"
                  allowfullscreen
                  controls
                ></b-embed>
                <youtube
                  id="youtube"
                  :video-id="video.embedId"
                  ref="youtube"
                  playsinline
                  :player-vars="playerVars"
                  disablepictureinpicture
                  controlslist="nodownload"
                  fitParent
                ></youtube>
              </div>
              <div class="row ml-0">
                <div class="video__uploaded-date" variant="warning">
                  Upload On {{ toDateFormat(video.uploadDate) }}
                </div>
                <div class="video__views">{{ video.videoViews }} views</div>
              </div>
              <div class="row ml-0">
                <div class="video__title clearfix">{{ videoTitle }}</div>
              </div>
              <div class="row ml-0">
                <div class="video__upload-by">By {{ video.uploadBy }}</div>
              </div>
            </div>
            <b-button
              size="sm"
              class="video__delete"
              variant="secondary"
              @click="confirmDelete(video.id)"
            >
              <i :class="'icon-bin'"></i> Delete Video
            </b-button>
          </b-col>
          <b-col>
            <div class="mb-2 text-right">
              <a :href="`/workout/Demo?id=${video.id}&tryNow=true`" target="_blank" class="btn btn-success">Try Now</a>
            </div>
            <div class="row">
              <div class="col-md-6">
                <b-form-group
                    v-if="video.id"
                    label="Video ID"
                    label-size="sm"
                    label-class="form__label">
                  <span class="form__label-star">*</span>
                  <b-form-input
                      v-model="video.id"
                      required
                      size="sm"
                      trim
                      disabled
                  ></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group
                    label="Video Visibility"
                    label-size="sm"
                    label-class="form__label">
                  <b-form-select
                      v-model="video.isPublic"
                      :options="[{value: true, text: 'Public'}, {value: false, text: 'Private'}]"
                      required
                      size="sm">
                  </b-form-select>
                </b-form-group>
              </div>
            </div>
            <b-form-group
              label="Video Title"
              label-size="sm"
              label-class="form__label"
            >
              <span class="form__label-star">*</span>
              <b-form-input
                v-model="video.title"
                required
                size="sm"
                placeholder="e.g.: High Knees by The Active Helath"
                trim
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="Upload Thumbnail"
              label-size="sm"
              label-class="form__label"
            >
              <b-row class="w-100">
                <b-col cols="4">
                  <b-img
                    :src="thumbnailSrc"
                    height="50"
                    width="100"
                    left
                  ></b-img>
                </b-col>
                <b-col cols="8">
                  <b-button
                    size="sm"
                    variant="success"
                    @click="
                      $refs.fileInput.$el
                        .querySelector('input[type=file]')
                        .click()
                    "
                    >Select Files</b-button
                  >
                  <b-form-file
                    class="form___file"
                    ref="fileInput"
                    v-model="file"
                    :state="Boolean(file)"
                    placeholder="Select Files"
                    size="sm"
                    accept="image/*"
                    @change="handleImageChange($event)"
                  ></b-form-file>
                </b-col>
              </b-row>
            </b-form-group>
            <b-row class="form__row" v-if="privileges.includes('write_video_data')">
              <b-col>
                <b-form-group
                  label="Video Start Time"
                  label-size="sm"
                  label-class="form__label"
                >
                  <span class="form__label-star">*</span>
                   <b-form-input
                      v-model="startTime"
                      required
                      size="sm"
                      trim
                      minlength="1"
                      maxlength="6"
                      @keydown="timeValidator"
                      @click.right.prevent
                      @paste.prevent
                    ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col>
                <b-form-group
                  label="Video End Time"
                  label-size="sm"
                  label-class="form__label"
                >
                  <span class="form__label-star">*</span>
                  <b-form-input
                    v-model="endTime"
                    required
                    size="sm"
                    trim
                    minlength="1"
                    maxlength="6"
                    @keydown="timeValidator"
                    @click.right.prevent
                    @paste.prevent
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group
              v-if="privileges.includes('write_video_data')"
              label="Video Intensity"
              label-size="sm"
              label-class="form__label"
            >
              <span class="form__label-star">*</span>

              <b-form-input
                id="range-1"
                v-model="intensity"
                type="range"
                min="1"
                max="20"
                step="0.5"
                size="sm"
                ref="videoIntensity"
                class="intensity col-6 pl-3"
              >
              </b-form-input>
              <div class="video-intensity-val d-flex col-3">
                {{ intensity }}
              </div>
            </b-form-group>
            <b-form-group
              v-if="privileges.includes('write_video_data')"
              label="Video Baseline Folder"
              label-size="sm"
              label-class="form__label"
            >
              <span class="form__label-star">*</span>
              <b-form-input
                v-model="video.baseline"
                required
                size="sm"
                trim
                maxlength="100"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              v-if="privileges.includes('write_scoring_logic')"
              label="Scoring Logic"
              label-size="sm"
              label-class="form__label"
            >
              <span class="form__label-star">*</span>
              <b-form-select
                v-model="video.scoringLogic"
                value-field="id"
                text-field="name"
                :options="scoringLogics"
                required
                size="sm"
              ></b-form-select>
            </b-form-group>
            
          </b-col>
        </b-row>
        <hr />
        <b-row>
          <b-col cols="9"></b-col>
          <b-col cols="3">
            <b-button
              class="form__button_left"
              size="sm"
              variant="light"
              @click="cancel()"
              >Cancel</b-button
            >
            <b-button
              class="form__button_right"
              size="sm"
              variant="success"
              type="submit"
              >Save</b-button
            >
          </b-col>
        </b-row>
      </form>
      <loader v-else></loader>
    </div>

    <div class="my-3">
      <hr>
      <div class="row">
        <div class="col-md-4">
          <h4 class="mb-3 text-white">Video Status</h4>
          <b-list-group class="list-group-flush" v-if="videoToEdit.videoStatus">
            <b-list-group-item
                v-for="status in videoToEdit.videoStatus" :variant="status.status === 'failed'? 'danger' : ''"
                class="d-flex justify-content-between align-items-center font-14">
              {{ status.name }}
              <b-badge
                  class="text-capitalize font-weight-normal font-12"
                  :variant="getStatusVariant(status.status)"
                  pill>
                {{ status.status }}
              </b-badge>
            </b-list-group-item>
          </b-list-group>
          <div v-else>
            Status not available
          </div>
        </div>
        <div>
        <div class="table-wrapper-scroll-y my-custom-scrollbar">
          <h4 class="mb-3 text-white">Automation Testing Result</h4>
          <table class="align-items-center font-14 table table-bordered table-striped mb-0 width=100%">
            <thead>
              <tr>
                <th>Test Type</th>
                <th>Score</th>
                <th>Test Video Id</th>
                <th>Scoring Logic</th>
                <th>Time Taken (Mins)</th>
                <th>Last Tested</th>
              </tr>
            </thead>
            <tbody>
              <tr class="font-15 test_results" v-for="test in videoTestResult">
                <th>{{ test.type }}</th>
                <th>{{ test.score }}</th>
                <th>{{ test.tested_with }}</th>
                <th>{{ test.scoring_logic}}</th>
                <th>{{ test.time_taken }} </th>
                <th>{{ test.last_tested }}</th>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>    
    <hr>
  </div>
  <loader v-if="editingVideo || deletingVideo"></loader>
  </div>
</template>
<script>
import toast from "../toaster.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters, mapActions } = createNamespacedHelpers("admin/videosView");

import S3Url from "../s3upload/getSignedURL.js";
import {VIDEO_DATA} from "../utils/constants";

export default {
  data: function() {
    return {
      privileges: null,
      file: null,
      thumbnail: null,
      thumbnailSrc: null,
      videoTitle: null,
      data: VIDEO_DATA,
      intensity: 1,
      scoringLogic: 9,
      editingVideo: false,
      deletingVideo: false,
      jsonDataPlaceholder: `Please enter data in this format
      {
        "steps": [],
        "backendSteps": []
      }`,
      playerVars: {
        autoplay: 0,
        controls: 1,
        playsinline: 1,
      },
      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      isStitchable: false
    };
  },

  methods: {
    ...mapActions([
      "getVideoById",
      "deleteVideo",
      "updateVideo",
      "getScoringLogic",
      "getS3Cred",
      "getVideoTestingResult",
    ]),
    timeValidator(event){
      let keyCode = event.keyCode ? event.keyCode : event.which;
      if(keyCode == 8){
        return;
      }
      if (
        (!/^[0-9]+$/.test(event.key))
      ) {
        event.preventDefault();
      }
    },
    async videoUpdate() {
      this.video.isStitchable = this.isStitchable;
      this.video.data = this.data;
      this.video.scoringLogic = [this.video.scoringLogic];
      if (this.isJson(this.video.data)) {
        this.editingVideo = true;
        if (this.file) {
          //upload thumbnail to s3
          await this.getS3Cred();
          let s3Cred = this.$store.state.admin.videosView.s3Cred;
          let thumbResponse = await S3Url.getS3Url(
            s3Cred,
            this.file,
            this.thumbnail,
            "thumbnails"
          );
          this.thumbnail = thumbResponse.Location;
        }
        this.updateVideo({
          vm: this,
          video: this.video,
          thumbnail: this.thumbnail,
          intensity: this.intensity,
        }).then(() => {
          this.editingVideo = false;
        });
      }
    },

    isJson(str) {
      try {
        JSON.parse(JSON.stringify(str));
      } catch (e) {
        toast.showToasts(this, "Please check the JSON format.", "warning");
        return false;
      }
      return true;
    },

    confirmDelete(video) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to delete this video?", {
          modalClass: "confirm__modal",
          contentClass: "confirm__modal-contents",
          title: "Please Confirm",
          size: "sm",
          buttonSize: "sm",
          cancelVariant: "light",
          okTitle: "YES",
          cancelTitle: "NO",
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.deleteVideo({ vm: this, id: video });
          }
        })
        .catch((err) => {
          // An error occurred
        });
    },

    handleImageChange(e) {
      this.createBase64Img(e.target.files[0]);
    },

    createBase64Img(file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        this.thumbnail = e.target.result;
        this.thumbnailSrc =
          "data:image/jpeg;base64," +
          btoa(
            new Uint8Array(this.thumbnail).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ""
            )
          );
      };

      reader.readAsArrayBuffer(file);
    },

    toDateFormat(uploadDate) {
      const date = new Date(uploadDate);
      return (
        this.months[date.getMonth()] +
        " " +
        date.getDate() +
        " " +
        date.getFullYear()
      );
    },

    cancel() {
      this.$router.push("/admin/videos");
    },

    getStatusVariant(status) {
      switch (status) {
        case "success":
          return "success";
        case "pending":
          return "secondary";
        case "failed":
          return "danger";
        case "started":
          return "info";
        default:
          return "light";
      }
    }
  },

  computed: {
    ...mapGetters(["videoToEdit", "scoringLogics", "videoTestResult"]),

    video() {
      this.videoTitle = this.videoToEdit.title;
      this.thumbnail = this.videoToEdit.thumbnail;
      this.thumbnailSrc = this.videoToEdit.thumbnail;
      this.intensity = isNaN(this.videoToEdit.intensity)
        ? "6"
        : this.videoToEdit.intensity;
      if(this.videoToEdit?.data?.start){
        this.data.start = +this.videoToEdit.data.start;
        this.data.steps[0].start = +this.videoToEdit.data.start;
      }
      else{
        this.data.start = 0;
        this.data.steps[0].start = 0;
      }
      if(this.videoToEdit?.data?.end){
        this.data.end = +this.videoToEdit.data.end;
        this.data.steps[0].end = +this.videoToEdit.data.end;
      }
      else{
        this.data.end = 0;
        this.data.steps[0].end = 0;
      }
      this.videoToEdit.scoringLogic = this.videoToEdit.scoringLogic.length ? this.videoToEdit.scoringLogic[0] : this.scoringLogic;
      this.isStitchable = this.videoToEdit.isStitchable ?? false;
      return JSON.parse(JSON.stringify(this.videoToEdit));
    },
    startTime: {
      get() {
        return this.data.start;
      },
      set(newValue) {
        this.data.start = +newValue;
        this.data.steps[0].start = +newValue;
      }
    },
    endTime:{
      get() {
        return this.data.end;
      },
      set(newValue) {
        this.data.end = +newValue;
        this.data.steps[0].end = +newValue;
      }
    },

    youtubeURL() {
      return "https://www.youtube.com/embed/" + this.videoToEdit.embedId;
    }
  },

  created() {
    this.privileges = this.$store.state.userInfo.privileges;

    if (this.privileges.indexOf("write_scoring_logic") > -1) {
      this.getScoringLogic();
    }
    this.getVideoById(this.$route.params.id);

    this.getVideoTestingResult(this.$route.params.id);
  },
};
</script>
<style scoped>
.video__checkbox{
  display: flex;
  column-gap: 1em;
}
.video {
  width: 100%;
  height: auto;
  float: left;
  padding: 15px;
}

.video__uploaded-date {
  float: left;
  color: orange;
  width: 84%;
  font-size: 13px;
  padding: 6px 0;
}

.video__title {
  text-transform: capitalize;
  font-weight: bold;
  font-size: 20px;
}
.video__views {
  float: right;
  font-size: 13px;
  padding: 6px 0;
  color: gray;
}

.video__upload-by {
  font-size: 13px;
  color: gray;
  padding: 6px 0;
}

.video__delete {
  margin-left: 3%;
}

.form___file {
  display: none;
}

.video-intensity-val {
  float: right;
  width: 48%;
  font-size: 16px;
  color: #73bc44;
  font-weight: bold;
}

.test_results {
  text-transform: uppercase;
}
.my-custom-scrollbar {
position: relative;
height: 320px;
overflow: auto;
}
.table-wrapper-scroll-y {
display: block;
}
</style>
